/**
 * Répartition par type d'action — barres horizontales.
 *
 * Catégories NOMINALES : les colorer une par une dépenserait le canal de
 * l'identité pour ré-encoder ce que la longueur de la barre dit déjà, et
 * imposerait jusqu'à dix teintes distinctes — bien au-delà de ce qu'une palette
 * sûre peut porter. Toutes les barres prennent donc l'emplacement 1, et il n'y
 * a pas de légende : une seule couleur, le titre suffit.
 *
 * Horizontal et non vertical : les libellés (« Connexion Wi-Fi »,
 * « Transcription ») sont longs, à la verticale ils s'inclineraient.
 *
 * Les valeurs sont posées au bout des barres, à l'extérieur — un libellé placé
 * à l'intérieur d'une barre courte serait rogné.
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { actionLabel } from '@shared/types/stats';

import { formatCompact, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { ActionBreakdownItem, DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';

interface Row extends ActionBreakdownItem {
  label: string;
}

const columns: ReadonlyArray<DataColumn<Row>> = [
  { key: 'label', header: 'Action', render: (row) => row.label },
  { key: 'actions', header: 'Actions', numeric: true, render: (row) => formatInteger(row.actions) },
  { key: 'share', header: 'Part', numeric: true, render: (row) => formatPercent(row.share) },
];

export function ActionBreakdownChart({ data }: { data: readonly ActionBreakdownItem[] }) {
  // `actionLabel` retombe sur la valeur brute pour une action inconnue du
  // front : le jour ou une valeur est ajoutee a l'enum en prod, elle s'affiche
  // telle quelle plutot que de disparaitre du graphique.
  const rows: Row[] = data.map((item) => ({ ...item, label: actionLabel(item.actionName) }));

  // Hauteur proportionnelle au nombre de catégories : à dix actions, la
  // hauteur par défaut de 260 px écraserait les barres les unes sur les
  // autres. Elle est passée à la carte, qui dimensionne sa zone de tracé —
  // forcer la hauteur à l'intérieur ferait déborder le contenu du cadre.
  const height = Math.max(200, rows.length * 34 + 40);

  return (
    <ChartCard
      title="Répartition par type d'action"
      subtitle="Part de chaque action dans le total de la période"
      chartHeight={height}
      rows={rows}
      columns={columns}
    >
      <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            layout="vertical"
            margin={{ top: 4, right: 56, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke={COLORS.grid} horizontal={false} />
            <XAxis
              type="number"
              tickFormatter={formatCompact}
              tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
              axisLine={false}
              tickLine={false}
              width={120}
            />
            <Tooltip
              // Sur des barres, la marque EST la cible : pas de reticule, un
              // simple voile sur la barre survolee.
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
              content={<ChartTooltip valueFormatter={formatInteger} />}
            />
            <Bar
              isAnimationActive={false}
              dataKey="actions"
              name="Actions"
              fill={COLORS.series1}
              // Arrondi cote donnee, carre sur la ligne de base.
              radius={[0, 4, 4, 0]}
              maxBarSize={BAR_MAX_SIZE}
            >
              <LabelList
                dataKey="actions"
                position="right"
                fill={COLORS.textSecondary}
                fontSize={12}
                formatter={(value: unknown) => formatInteger(Number(value))}
              />
            </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
